import React, { useEffect, useState, useCallback } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Helmet from 'react-helmet';
import dayjs from 'dayjs';

import { ApiServiceServerless } from '../xhr_libs';
import Loader from '../components/Loader';
import NoOrganizationsLink from '../components/NoOrganizationsLink';
import AddUtilityAccountModal from '../components/modals/UtilityAccountModals/AddUtilityAccountModal';
import AddBuildingModal from '../components/modals/BuildingModals/AddBuildingModal';
import PageHeader from '../components/PageHeader';
import EnergyTracerTable, { AddEntityFilterHeader } from '../components/tables';
import { linkFormatter } from '../components/tables/formatters';
import {
  selectFiltering,
  textFilterIncludes,
} from '../components/tables/filters';
import { useOrganizationRoles } from '../helpers/checkRoles';
import { getUserPreference } from '../helpers/user-preferences';
import { filterMaskedAccounts } from '../helpers/resourceFilters';

const PAGE_TITLE = 'Account Management';

const UTILITY_TYPES_OPTS = {
  0: 'Show all',
  1: 'Electric',
  2: 'Gas',
  3: 'Propane',
  4: 'Water',
  5: 'Production',
};

const getUniqueBuildings = (rows) => [
  ...new Map(rows.map((row) => [row.building.id, row.building])).values(),
];

const formatAccountType = (acctType) =>
  acctType
    ? acctType.charAt(0).toUpperCase() + acctType.substr(1).toLowerCase()
    : '';

const concatEmptyBuildingsToAccts = (accts, emptyBuildings) =>
  emptyBuildings
    .map((bldg) => ({
      buildings: [{ id: bldg.id, name: bldg.name }],
    }))
    .concat(accts);

const formatTableData = (accounts) => {
  const tableRows = accounts.flatMap((acct) =>
    // create a unique row per account-building pair
    acct.buildings.map((building) => ({
      ...acct,
      building: building,
      account_number: acct.account_number || '',
      account_type: formatAccountType(acct.account_type),
      last_updated: acct.max_bill_date,
      rowKey: `acct-${acct.id}-type-${acct.account_type}-bldg-${building.id}`,
    }))
  );
  return indicateFirstRows(tableRows);
};

const indicateFirstRows = (rows) => {
  const checkIfFirstAccountRow = (row, idx) => {
    if (!row.id || row.id !== currentAccountId) {
      currentAccountId = row.id;
      currentType = row.account_type;
      row.firstAccountRow = true;
      row.firstTypeRow = true;
      return row;
    } else row.firstAccountRow = false;
  };

  const checkIfFirstTypeRow = (row) => {
    if (row.account_type && row.account_type !== currentType) {
      row.firstTypeRow = true;
      currentType = row.account_type;
    } else row.firstTypeRow = false;
    return row;
  };

  let currentAccountId,
    currentType = null;
  return rows.map(
    (row, idx) => checkIfFirstAccountRow(row, idx) || checkIfFirstTypeRow(row)
  );
};

const getTableColumns = (
  showBuildingModal,
  showAccountModal,
  tableDataLength,
  buildingCreateAccess,
  utilityAccountCreateAccess,
  organization_id
) => [
  {
    dataField: 'account_number',
    text: 'Account #',
    filter: textFilterIncludes(
      'Account #',
      'account_number',
      indicateFirstRows
    ),
    filterRenderer: (onFilter) => (
      <AddEntityFilterHeader
        onFilter={onFilter}
        filterText='Filter accounts'
        buttonText='New Account'
        onButtonClick={showAccountModal}
        disabled={!tableDataLength}
        showButton={utilityAccountCreateAccess}
      />
    ),
    formatter: linkFormatter(
      (row) =>
        row.account_number
          ? [
              `/accounts/${row.id}`,
              row.firstAccountRow || row.firstTypeRow ? row.account_number : '',
            ]
          : ['', 'No Accounts'],
      (row) => ['last_selected_account', row.id]
    ),
    style: (_cell, row) => columnStyle(row, 'account_number'),
    classes: (_cell, row) => columnClasses(row),
  },
  {
    dataField: 'account_type',
    text: 'Utility Type',
    filter: selectFiltering(
      'Utility Type',
      UTILITY_TYPES_OPTS,
      'account_type',
      true,
      indicateFirstRows,
      'account_type_filter'
    ),
    style: (_cell, row) => columnStyle(row, 'account_type'),
    classes: (_cell, row) => columnClasses(row),
    formatter: (_cell, row) => (row.firstTypeRow ? row.account_type : ''),
  },
  {
    dataField: 'last_updated',
    text: 'Last Updated',
    formatter: (_cell, row) =>
      !row.firstTypeRow || !row.account_number ? (
        ''
      ) : !row.last_updated ? (
        <span className='text-secondary'>No Bills</span>
      ) : (
        dayjs.utc(row.last_updated).format('YYYY-MM-DD')
      ),
    style: (_cell, row) => columnStyle(row, 'last_updated'),
    classes: (_cell, row) => columnClasses(row),
  },
  {
    dataField: 'building.name',
    text: 'Building',
    filter: textFilterIncludes('Building', 'building.name', indicateFirstRows),
    filterRenderer: (onFilter) => (
      <AddEntityFilterHeader
        onFilter={onFilter}
        filterText='Filter buildings'
        buttonText='New Building'
        onButtonClick={showBuildingModal}
        showButton={buildingCreateAccess}
      />
    ),
    formatter: linkFormatter((row) => [
      `/buildings/${row.building.id}`,
      row.building.name,
      '/accounts',
    ]),
    style: (_cell, row) => columnStyle(row, 'building'),
    classes: (_cell, row) => columnClasses(row),
  },
];

const columnClasses = (row) => {
  return row.id === getUserPreference('last_selected_account')
    ? 'et-table-selected-row'
    : '';
};

const columnStyle = (row, dataField) => {
  const styles = {
    borderless: {
      borderBottomWidth: 0,
      borderTopWidth: 0,
    },
    borderThick: {
      borderTopWidth: 2,
    },
  };
  let ret_styles = {};
  if (row.firstAccountRow || row.firstTypeRow) {
    ret_styles = addStyles(ret_styles, styles.borderThick);
  }
  if (
    (dataField === 'account_number' ||
      ['account_type', 'last_updated'].includes(dataField)) &&
    !row.firstTypeRow
  ) {
    ret_styles = addStyles(ret_styles, styles.borderless);
  }
  return ret_styles;
};

const addStyles = (current, styles) => {
  Object.keys(styles).forEach((style) => {
    current[style] = styles[style];
  });
  return current;
};

function Accounts({
  showToast,
  userSelectedOrganization,
  userSelectedOrganizationDetails,
  history,
  isAdmin,
}) {
  const [organizations, setOrganizations] = useState([]);

  const [accounts, setAccounts] = useState([]);
  const [emptyBuildings, setEmptyBuildings] = useState([]);

  const [buildings, setBuildings] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showBuildingModal, setShowBuildingModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [defaultBuildings, setDefaultBuildings] = useState([16]);

  const buildingCreateAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'building',
    'create'
  );
  const utilityAccountCreateAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'utility_account',
    'create'
  );

  const tableColumns = getTableColumns(
    () => setShowBuildingModal(true),
    () => setShowAccountModal(true),
    tableData.length,
    buildingCreateAccess,
    utilityAccountCreateAccess,
    userSelectedOrganization.id
  );

  const refreshTableData = useCallback(
    (accounts, emptyBuildings) => {
      let parsed_accts = filterMaskedAccounts(accounts, isAdmin);
      const localTableData = formatTableData(
        concatEmptyBuildingsToAccts(parsed_accts, emptyBuildings)
      );
      setTableData(localTableData);
      setBuildings(getUniqueBuildings(localTableData));
    },
    [isAdmin]
  );

  useEffect(() => {
    refreshTableData(accounts, emptyBuildings);
  }, [accounts, emptyBuildings, refreshTableData]);

  const handleRefreshGetData = useCallback(async (org_id) => {
    setIsLoading(true);
    const [{ data: accounts }, { data: emptyBuildings }] = await Promise.all([
      ApiServiceServerless.get(`/utility_accounts/organization/${org_id}`, {
        authorization_id: org_id,
      }),
      ApiServiceServerless.get(`/buildings/organization/${org_id}?empty=true`, {
        authorization_id: org_id,
      }),
    ]);
    setAccounts(accounts);
    setEmptyBuildings(emptyBuildings);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setAccounts([]);
    setEmptyBuildings([]);
    setBuildings([]);
    setTableData([]);
    if (userSelectedOrganization.id) {
      handleRefreshGetData(userSelectedOrganization.id);
    } else {
      setIsLoading(false);
    }
  }, [handleRefreshGetData, userSelectedOrganization.id]);

  useEffect(() => {
    const row = document.querySelector('.et-table-selected-row');
    if (row) {
      const table = document.querySelector('.et-table-wrapper');
      const head = document.querySelector('.et-table-header');
      const subHead = document.querySelector('.et-table-header + tbody');
      if (table && head && subHead) {
        const offset = head.offsetHeight + subHead.offsetHeight;
        table.scrollTop = row.offsetTop - offset;
      }
    }
  });

  const handleSetNewBuilding = (data) => {
    setEmptyBuildings((prev) => [...prev, data]);
  };

  const handleSetAccounts = (data) => {
    setAccounts((prev) => [...prev, data]);
    handleUpdateEmptyBuildings(data);
  };

  const handleUpdateEmptyBuildings = (acc) => {
    setEmptyBuildings((prev) =>
      prev.filter((building) => !acc.building_ids.includes(building.id))
    );
  };

  return (
    <Container className='et-container'>
      <Helmet>
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <PageHeader
        title={PAGE_TITLE}
        showOrganizationSelect={true}
        showPortfolioSelect={true}
        setOrganizations={setOrganizations}
        isLoading={isLoading}
      />
      <Row>
        <Col xs={12}>
          {isLoading ? (
            <Loader style={{ marginTop: '8vh' }} />
          ) : !organizations.length ? (
            <NoOrganizationsLink />
          ) : (
            <>
              <EnergyTracerTable
                data={tableData}
                columns={tableColumns}
                enableFilter={true}
                keyField={'rowKey'}
                enableScroll={true}
              />
              {!tableData.length && !!organizations.length && (
                <div style={{ margin: '30px' }}>
                  {`There are no buildings within the ${userSelectedOrganization.name} organization. Please create a new building to add utility accounts.`}
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
      <AddBuildingModal
        organization={userSelectedOrganization}
        organizations={[userSelectedOrganization]}
        show={showBuildingModal}
        onHide={() => setShowBuildingModal(false)}
        openBuildingModal={() => {
          setShowBuildingModal(true);
        }}
        handleSetBuilding={handleSetNewBuilding}
        setDefaultBuildings={setDefaultBuildings}
        openAccountModal={() => setShowAccountModal(true)}
        addAccountOption={true}
        history={history}
        showToast={showToast}
      />
      <AddUtilityAccountModal
        organization={userSelectedOrganization}
        buildings={buildings}
        defaultBuildings={defaultBuildings}
        setDefaultBuildings={setDefaultBuildings}
        show={showAccountModal}
        onHide={() => {
          setShowAccountModal(false);
          setDefaultBuildings([]);
        }}
        openAccountModal={() => setShowAccountModal(true)}
        handleSetAccount={handleSetAccounts}
        parentIsLoading={isLoading}
        showToast={showToast}
        history={history}
      />
      <div style={{ marginBottom: '20px' }} />
    </Container>
  );
}

export default Accounts;
